/**
 * AppStatusContext — Strata deploy/update notification system (iter-156bu).
 *
 * One global poll loop instead of per-page. Two triggers:
 *   • setInterval every 60s.
 *   • visibilitychange → poll on tab-focus so a user returning from a
 *     two-hour Slack break sees the update modal immediately.
 *
 * State exposed to consumers:
 *   • appStatus            — full server payload
 *   • needsRefresh         — boolean: show UpdateModal
 *   • maintenance          — boolean: show MaintenanceModal + disable writes
 *   • forceLogout          — boolean: server is asking for re-login
 *   • requestRefreshOnce() — used by ChunkLoadError handler (single auto-attempt)
 *
 * IMPORTANT: this module is loaded BEFORE auth, so the public
 * /api/app/status read uses a plain axios call without withCredentials.
 */
import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BUILD_VERSION = process.env.REACT_APP_BUILD_VERSION || '';
const POLL_INTERVAL_MS = 60_000;

const AppStatusContext = createContext({
  appStatus: null,
  needsRefresh: false,
  maintenance: false,
  forceLogout: false,
  requestRefreshOnce: () => {},
  showMaintenance: () => {},
  showUpdateRequired: () => {},
  showSessionStale: () => {},
});

// String-based "newer than" comparison. We use ISO-ish version strings
// (build timestamps like "20260602.1530" or "1.2.3"). String compare is
// safe when versions are zero-padded; we keep it simple.
const isFrontendStale = (serverMin, mine) => {
  if (!serverMin) return false;       // server has no minimum set
  if (!mine) return false;            // we don't know our version — don't nag
  return String(mine) < String(serverMin);
};

export const AppStatusProvider = ({ children }) => {
  const [appStatus, setAppStatus] = useState(null);
  const [forceUpdateModal, setForceUpdateModal] = useState(false);
  const [maintenanceModal, setMaintenanceModal] = useState(false);
  const [sessionStaleModal, setSessionStaleModal] = useState(false);
  // iter-156ep — `reloadAttemptedRef` was the bookkeeping for the
  // old auto-reload-on-ChunkLoadError path. We no longer auto-reload,
  // so the ref is unused. Kept as an empty placeholder so the API
  // shape stays stable for any external consumer that still reads it
  // (none in tree — safe to remove next refactor).
  const reloadAttemptedRef = useRef(false);  // noqa: deprecated
  // iter-156ca — Tab-baseline check. The FIRST app_version we
  // observe during this page-load session becomes our baseline.
  // Any subsequent poll that returns a DIFFERENT app_version means
  // the backend was redeployed while this tab was open → surface
  // the update modal. Resets naturally on refresh because a fresh
  // tab starts with `null` baseline.
  const baselineVersionRef = useRef(null);
  const [versionDrift, setVersionDrift] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      // iter-156ck — tag the polling status check so we can see it
      // separately from any user-driven /api/app/status reads.
      const { data } = await axios.get(`${API}/app/status`, {
        headers: { 'X-Trigger': 'poll:app-status' },
      });
      setAppStatus(data);
      const v = data?.app_version || '';
      if (v) {
        if (baselineVersionRef.current === null) {
          baselineVersionRef.current = v;
        } else if (baselineVersionRef.current !== v) {
          // Backend redeployed while this tab was open.
          setVersionDrift(true);
        }
      }
    } catch (e) {
      // Backend unreachable. Don't toggle modals — user might be
      // mid-action when the network blips. The next 60s tick retries.
    }
  }, []);

  // Initial + interval + visibility poll.
  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, POLL_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchStatus();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchStatus]);

  // Derived booleans
  const needsRefresh = useMemo(() => {
    if (!appStatus) return false;
    if (appStatus.force_refresh) return true;
    if (isFrontendStale(appStatus.min_supported_frontend_version, BUILD_VERSION)) return true;
    // iter-156ca — tab-baseline drift. Triggered when the backend's
    // `app_version` changes mid-session (auto-bumped on every pod
    // restart / deploy). Independent of `min_supported_frontend_version`
    // so it fires even when the frontend bundle's BUILD_VERSION is
    // hardcoded.
    if (versionDrift) return true;
    return forceUpdateModal;
  }, [appStatus, forceUpdateModal, versionDrift]);

  const maintenance = useMemo(() => {
    if (maintenanceModal) return true;
    return !!appStatus?.maintenance_mode;
  }, [appStatus, maintenanceModal]);

  const forceLogout = !!appStatus?.force_logout || sessionStaleModal;

  // Called by axios interceptor when an API returns SESSION_STALE.
  const showSessionStale = useCallback(() => setSessionStaleModal(true), []);
  const showMaintenance = useCallback(() => setMaintenanceModal(true), []);
  const showUpdateRequired = useCallback(() => setForceUpdateModal(true), []);

  // ChunkLoadError handler.
  //
  // iter-156ep — Never force-reload. Previously the FIRST chunk-load
  // failure auto-reloaded the page, which dumped any in-progress
  // work that hadn't been autosaved yet. Now we ALWAYS surface the
  // soft banner via `needsRefresh` and let the user choose when to
  // refresh — the autosave layer (iter-156eo) keeps their work safe
  // in the meantime. The previous `reloadAttemptedRef` /
  // `sessionStorage` cross-reload bookkeeping is no longer needed
  // because we don't auto-reload at all.
  const requestRefreshOnce = useCallback(() => {
    setForceUpdateModal(true);
  }, []);

  // iter-156ep — Stale: this used to restore the "already auto-
  // reloaded" flag across the reload boundary. Since we no longer
  // auto-reload (we surface the soft banner instead), this is dead
  // code; left as a defensive no-op until next refactor cleans up
  // the unused ref + sessionStorage key.
  useEffect(() => {
    try {
      sessionStorage.removeItem('strata:chunk-reload-attempted');
    } catch {
      /* private mode / quota — noop */
    }
  }, []);

  const value = useMemo(() => ({
    appStatus,
    needsRefresh,
    maintenance,
    forceLogout,
    requestRefreshOnce,
    showMaintenance,
    showUpdateRequired,
    showSessionStale,
    buildVersion: BUILD_VERSION,
  }), [appStatus, needsRefresh, maintenance, forceLogout, requestRefreshOnce, showMaintenance, showUpdateRequired, showSessionStale]);

  return <AppStatusContext.Provider value={value}>{children}</AppStatusContext.Provider>;
};

export const useAppStatus = () => useContext(AppStatusContext);

/**
 * Imperative helpers for the axios interceptor (which sits OUTSIDE
 * the React tree). The provider above sets these on mount.
 */
let _imperativeHandlers = {
  onSessionStale: () => {},
  onMaintenance: () => {},
  onUpdateRequired: () => {},
};
export const setAppStatusHandlers = (h) => { _imperativeHandlers = { ..._imperativeHandlers, ...h }; };
export const getAppStatusHandlers = () => _imperativeHandlers;
